import auth, { AuthParams } from '@/utils/authentication';
import { useEffect, useMemo, useState } from 'react';
import {
  IconDashboard,
  IconBook,
  IconStorage,
  IconTag,
  IconSettings,
  IconRobot,
  IconSearch,
  IconApps,
  IconUser,
  IconCompass,
} from '@arco-design/web-react/icon';

// 声明全局window接口扩展
declare global {
  interface Window {
    triggerDeepSearchNavigation: () => void;
    triggerPolicyQANavigation: () => void;
  }
}

export type IRoute = AuthParams & {
  name: string;
  key: string;
  icon?: any;
  // 当前页是否展示面包屑
  breadcrumb?: boolean;
  children?: IRoute[];
  // 当前路由是否渲染菜单项，为 true 的话不会在菜单中显示，但可通过路由地址访问。
  ignore?: boolean;
  component?: string;
  // 页面标题，如果不设置则使用name
  title?: string;
  requiredPermissions?: {
    resource: string;
    actions: string[];
  }[];
  oneOfPerm?: string[];
};

export const routes: IRoute[] = [
  {
    name: '数据看板',
    key: 'dashboard',
    icon: IconDashboard,
    children: [
      {
        name: '工作台',
        key: 'dashboard/workplace',
        requiredPermissions: [
          {
            resource: 'dashboard/workplace',
            actions: ['*'],
          },
        ],
      },
      {
        name: '数据源统计',
        key: 'dashboard/statistics',
        requiredPermissions: [
          {
            resource: 'dashboard/statistics',
            actions: ['*'],
          },
        ],
      },
      {
        name: '药品销售统计',
        key: 'dashboard/drug-sales-statistics',
        icon: IconStorage,
        requiredPermissions: [
          {
            resource: 'dashboard/drug-sales-statistics',
            actions: ['*'],
          },
        ],
      },
    ],
  },
  {
    name: 'menu.contentSource',
    key: 'content-source',
    children: [
      {
        name: 'menu.contentSource.policy',
        key: 'content-source/policy',
        requiredPermissions: [
          {
            resource: 'content-source/policy',
            actions: ['*'],
          },
        ],
      },
      {
        name: '政策详情',
        key: 'content-source/policy-detail/:uuid',
        ignore: true, // 不在菜单中显示，但可通过URL访问
        breadcrumb: false,
        requiredPermissions: [
          {
            resource: 'content-source/policy',
            actions: ['*'],
          },
        ],
      },
      {
        name: 'menu.contentSource.policyCompare',
        key: 'content-source/policy-compare',
        requiredPermissions: [
          {
            resource: 'content-source/policy-compare',
            actions: ['*'],
          },
        ],
      },
      {
        name: 'menu.contentSource.knowledgeBase',
        key: 'content-source/knowledge-base',
        requiredPermissions: [
          {
            resource: 'admin',
            actions: ['*'],
          },
        ],
      },
      {
        name: 'menu.contentSource.subscription',
        key: 'content-source/subscription',
        requiredPermissions: [
          {
            resource: 'content-source/subscription',
            actions: ['*'],
          },
        ],
      },
      {
        name: 'menu.contentSource.favorite',
        key: 'content-source/favorite',
        requiredPermissions: [
          {
            resource: 'content-source/favorite',
            actions: ['*'],
          },
        ],
      },
    ],
  },
  {
    name: '医保数据',
    key: 'nhsa',
    icon: IconCompass,
    children: [
      {
        name: '药品目录',
        key: 'nhsa/drug-catalog',
        requiredPermissions: [
          { resource: 'nhsa/drug-catalog', actions: ['*'] },
        ],
      },
      {
        name: '配备机构',
        key: 'nhsa/drug-allocation',
        requiredPermissions: [
          { resource: 'nhsa/drug-allocation', actions: ['*'] },
        ],
      },
      {
        name: '医用耗材',
        key: 'nhsa/medical-supplies',
        requiredPermissions: [
          { resource: 'nhsa/medical-supplies', actions: ['*'] },
        ],
      },
      {
        name: '定点医疗',
        key: 'nhsa/hospital',
        requiredPermissions: [
          { resource: 'nhsa/hospital', actions: ['*'] },
        ],
      },
      {
        name: '定点药店',
        key: 'nhsa/shop',
        requiredPermissions: [
          { resource: 'nhsa/shop', actions: ['*'] },
        ],
      },
    ],
  },
  {
    name: '数据智能',
    key: 'data-intelligence',
    icon: IconRobot,
    children: [
      {
        name: '政策问答',
        key: 'data-intelligence/policy-qa',
        breadcrumb: false,
        requiredPermissions: [
          {
            resource: 'data-intelligence/policy-qa',
            actions: ['*'],
          },
        ],
      },
      // {
      //   name: 'AI 助手',
      //   key: 'data-intelligence/ai-assistant',
      //   breadcrumb: false,
      //   requiredPermissions: [
      //     {
      //       resource: 'data-intelligence/ai-assistant',
      //       actions: ['*'],
      //     },
      //   ],
      // },
      // {
      //   name: '深度搜索',
      //   key: 'data-intelligence/deep-search',
      //   breadcrumb: false,
      //   requiredPermissions: [
      //     {
      //       resource: 'data-intelligence/deep-search',
      //       actions: ['*'],
      //     },
      //   ],
      // },
      {
        name: '报告等待',
        key: 'data-intelligence/report-waiting',
        ignore: true,
        breadcrumb: false,
        requiredPermissions: [
          {
            resource: 'data-intelligence/report-waiting',
            actions: ['*'],
          },
        ],
      },
    ],
  },
  {
    name: 'menu.user',
    key: 'user',
    icon: IconUser,
    ignore: true,
    children: [
      {
        name: 'menu.user.setting',
        key: 'user/setting',
        requiredPermissions: [
          {
            resource: 'user/setting',
            actions: ['*'],
          },
        ],
      },
    ],
  },
  {
    name: 'menu.helpCenter',
    key: 'help-center',
    children: [
      {
        name: 'menu.changeLog',
        key: 'change-log',
      },
      {
        name: 'menu.termsOfService',
        key: 'terms-of-service',
      },
      {
        name: 'menu.privacyPolicy',
        key: 'privacy-policy',
      },
    ],
  },
];

export const getName = (path: string, routes) => {
  return routes.find((item) => {
    const itemPath = `/${item.key}`;
    if (path === itemPath) {
      return item.name;
    } else if (item.children) {
      return getName(path, item.children);
    }
  });
};

export const generatePermission = (role: string) => {
  const actions = role === 'admin' ? ['*'] : ['read'];
  const result = {};
  routes.forEach((item) => {
    if (item.children) {
      item.children.forEach((child) => {
        // 使用 key 而不是 name 作为权限标识
        result[child.key] = actions;
      });
    }
  });
  return result;
};

const useRoute = (userPermission): [IRoute[], string] => {
  const filterRoute = (routes: IRoute[], arr = []): IRoute[] => {
    if (!routes.length) {
      return [];
    }
    for (const route of routes) {
      const { requiredPermissions, oneOfPerm } = route;
      let visible = true;
      if (requiredPermissions) {
        visible = auth({ requiredPermissions, oneOfPerm }, userPermission);
      }

      if (!visible) {
        continue;
      }
      if (route.children && route.children.length) {
        const newRoute = { ...route, children: [] };
        filterRoute(route.children, newRoute.children);
        if (newRoute.children.length) {
          arr.push(newRoute);
        }
      } else {
        arr.push({ ...route });
      }
    }

    return arr;
  };

  const [permissionRoute, setPermissionRoute] = useState(routes);

  useEffect(() => {
    const newRoutes = filterRoute(routes);
    setPermissionRoute(newRoutes);
  }, [JSON.stringify(userPermission)]);

  const defaultRoute = useMemo(() => {
    const first = permissionRoute[0];
    if (first) {
      const firstRoute = first?.children?.[0]?.key || first.key;
      return firstRoute;
    }
    return '';
  }, [permissionRoute]);

  return [permissionRoute, defaultRoute];
};

export default useRoute;

// 添加导航触发器的全局初始化代码
// 此函数会在应用初始化时执行
(function initializeNavigationTriggers() {
  // 在切换路由时检查是否需要触发导航事件
  const handleRouteChange = () => {
    // 添加防循环检查 - 如果正在更新URL，则跳过处理
    if ((window as any).__updatingUrl) {
      console.log('[Routes] 检测到正在更新URL，跳过导航处理防止循环');
      return;
    }

    // 检查当前路径是否为深度搜索或政策问答
    const currentPath = window.location.pathname;

    if (
      currentPath.endsWith('/deep-search') ||
      currentPath.endsWith('/deep-search/')
    ) {
      // 如果是深度搜索页面，且全局函数存在，则触发导航事件
      if (typeof window.triggerDeepSearchNavigation === 'function') {
        console.log('[Routes] 触发深度搜索导航事件');
        window.triggerDeepSearchNavigation();
      }
    } else if (
      currentPath.endsWith('/policy-qa') ||
      currentPath.endsWith('/policy-qa/')
    ) {
      // 如果是政策问答页面，且全局函数存在，则触发导航事件
      if (typeof window.triggerPolicyQANavigation === 'function') {
        console.log('[Routes] 触发政策问答导航事件');
        window.triggerPolicyQANavigation();
      }
    }
  };

  // 监听路由变化事件
  window.addEventListener('popstate', handleRouteChange);

  // 重写history.pushState和replaceState方法以捕获SPA内部路由变化
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    originalPushState.apply(this, args);
    // 添加防循环检查
    if (!(window as any).__updatingUrl) {
      handleRouteChange();
    }
  };

  window.history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    // 添加防循环检查
    if (!(window as any).__updatingUrl) {
      handleRouteChange();
    }
  };
})();
